import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Mail, AlertCircle, CheckCircle, ArrowLeft, Lock } from 'lucide-react';

const PasswordResetRequest: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // CRITICAL FIX: Always use production domain for password reset redirects
      // This ensures users receive the correct reset link regardless of where the request originates
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      const redirectTo = isProduction 
        ? 'https://quote.evoqia.digital/auth/reset'
        : `${window.location.origin}/auth/reset`;
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (resetError) {
        setError(resetError.message);
        setLoading(false);
        return;
      }

      // Always show success message (security best practice - don't reveal if email exists)
      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Er is een onverwachte fout opgetreden');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Logo */}
      <header className="border-b border-gray-200">
        <div className="px-6 py-4">
          <img
            src="/assets/images/evoqia-logo_-nb-1766401973047.png"
            alt="Evoqia logo with geometric green and gray icon element and modern typography"
            className="h-10 object-contain"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#7CC363]/10 rounded-lg mb-4">
              <Lock className="h-6 w-6 text-[#7CC363]" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">Wachtwoord Vergeten</h1>
            <p className="text-gray-600">
              Voer uw e-mailadres in en we sturen u een link om uw wachtwoord opnieuw in te stellen
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 space-y-6">
            {success ? (
              // Success Message
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Controleer uw e-mail
                  </h3>
                  <p className="text-sm text-gray-600">
                    Als er een account bestaat met het opgegeven e-mailadres, ontvangt u binnen enkele minuten een e-mail met instructies om uw wachtwoord opnieuw in te stellen.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/user-authentication-login')}
                  className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] transition"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Terug naar inloggen
                </button>
              </div>
            ) : (
              // Reset Form
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                {/* Email Input */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mailadres
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                      placeholder="u@voorbeeld.nl"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7CC363] hover:bg-[#6AB352] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Versturen...
                    </>
                  ) : (
                    'Stuur reset mail'
                  )}
                </button>

                {/* Back to Login */}
                <button
                  type="button"
                  onClick={() => navigate('/user-authentication-login')}
                  className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] transition"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Terug naar inloggen
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetRequest;